import React from 'react';
import { CalendarBlank, ArrowRight } from '@phosphor-icons/react';

interface ActionListProps {
    data: Array<{
        action_type: string;
        item_name: string;
        due_date?: string;
        link?: string;
    }>;
    options?: {
        show_due_date?: boolean;
        show_action_type?: boolean;
        group_by_type?: boolean;
        max_items?: number;
    };
}

const formatActionType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getActionColor = (type: string) => {
    const colors: Record<string, string> = {
        review: 'bg-purple-100 text-purple-700',
        goal: 'bg-blue-100 text-blue-700',
        survey: 'bg-green-100 text-green-700',
        training: 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
};

export const ActionList: React.FC<ActionListProps> = ({ data, options = {} }) => {
    const {
        show_due_date = true,
        show_action_type = true,
        max_items = 10
    } = options;

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                    <CalendarBlank size={24} />
                </div>
                <span className="text-sm">No pending actions</span>
            </div>
        );
    }

    const items = data.slice(0, max_items);

    // Simple list view for now, grouping can be added if needed
    return (
        <div className="flex flex-col h-full overflow-auto">
            {items.map((item, index) => (
                <div
                    key={`${item.item_name}-${index}`}
                    className="flex items-center justify-between p-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        {show_action_type && (
                            <span className={`
                        px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0
                        ${getActionColor(item.action_type)}
                    `}>
                                {formatActionType(item.action_type)}
                            </span>
                        )}
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
                            {show_due_date && item.due_date && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                    <CalendarBlank size={12} />
                                    Due {new Date(item.due_date).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    </div>
                    <ArrowRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                </div>
            ))}
        </div>
    );
};
